import { RETURN_CODE_PARAM } from '@/line/auth/socialLoginForLineMiniApp/returnParams'

export const SENSITIVE_QUERY_PARAM_MASK = '[MASKED]'

/*
 * Sentry に送る前に値を伏せるクエリパラメータ名。
 * URL に秘密の値を載せる画面を追加したら、ここにクエリ名を足す。
 *
 * - lineSocialLoginCode: LINE ミニアプリ外部ブラウザ SNS ログインの「LINE に戻る」リンクの completionCode。
 *   ミニアプリはリンクから開くと読み込み直されるため、画面側で URL から消す前に
 *   ページ読み込み・画面遷移の記録に残りうる
 */
const SENSITIVE_QUERY_PARAMS = [RETURN_CODE_PARAM]

/*
 * `name=value` に加え、別のクエリの値に URL エンコードされて入っている形（%3D / %253D）にも一致させる。
 * 例: LINE ミニアプリの1回目の読み込みでは liff.state の中に遷移先 URL がエンコードされて入る。
 * 値は区切り文字（& # 空白 引用符 %）の手前までを伏せる。
 */
const SENSITIVE_QUERY_PATTERN = new RegExp(
  `((?:${SENSITIVE_QUERY_PARAMS.join('|')})(?:=|%3D|%253D))[^&#%\\s"']+`,
  'gi'
)

/* これより深いネストは見ない（Sentry のイベントは浅いが、念のため上限を設ける） */
const MAX_DEPTH = 10

/* 文字列の中の対象クエリの値だけを伏せる（クエリ名と他の部分は残す） */
const maskString = (value: string): string =>
  value.replace(SENSITIVE_QUERY_PATTERN, `$1${SENSITIVE_QUERY_PARAM_MASK}`)

/*
 * 値をたどって、中の文字列すべてに maskString をかけたコピーを返す。
 * 配列とプレーンなオブジェクトだけを再帰的にたどり、それ以外（数値・null・Error 等）はそのまま返す。
 * depth は現在のネストの深さで、MAX_DEPTH 以上は中を見ずにそのまま返す。
 */
const maskValue = (value: unknown, depth: number): unknown => {
  if (typeof value === 'string') return maskString(value)
  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') {
    return value
  }
  if (Array.isArray(value)) return value.map(v => maskValue(v, depth + 1))
  // Sentry に送るのは JSON 化できるプレーンなオブジェクトだけなので、それ以外（Error 等）は触らない
  if (Object.getPrototypeOf(value) !== Object.prototype) return value
  return Object.fromEntries(
    Object.entries(value).map(([k, v]) => [k, maskValue(v, depth + 1)])
  )
}

/*
 * Sentry に送るイベント・パンくずの中の文字列から、SENSITIVE_QUERY_PARAMS の値を伏せたコピーを返す。
 * 入力は変更しない。
 */
export const maskSensitiveQueryParams = <T>(value: T): T =>
  maskValue(value, 0) as T
